import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  KeyValuePairs,
  Link,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { cancelZipBlast, getZipBlast, retryZipBlast } from '../../../common/api/zipBlasts';
import { DashboardViewLayout } from '../../../components/DashboardViewLayout';
import { ZipBlastStatusBadge } from '../../../components/ZipBlastStatusBadge';

const TERMINAL_STATUSES = new Set(['complete', 'failed', 'cancelled']);

type ChildZipRow = {
  zip: string;
  role: string;
};

function formatDate(value?: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

function formatZipList(zips: string[]): string {
  return zips.length > 0 ? zips.join(', ') : '—';
}

function getScopeLabel(data: Awaited<ReturnType<typeof getZipBlast>>): string {
  if (!data) return 'Zip blast';
  if (data.area_label) return data.area_label;
  if (data.city || data.state) return [data.city, data.state].filter(Boolean).join(', ');
  return data.zip_code;
}

export default function ZipBlastDetailPage() {
  const { uuid } = useParams({ from: '/dashboard/zip-blasts_/$uuid' });
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['zip-blast', uuid],
    queryFn: () => getZipBlast(uuid),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status && TERMINAL_STATUSES.has(status) ? false : 5000;
    },
  });

  const cancelMut = useMutation({
    mutationFn: () => cancelZipBlast(uuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zip-blast', uuid] }),
  });
  const retryMut = useMutation({
    mutationFn: () => retryZipBlast(uuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zip-blast', uuid] }),
  });

  if (isLoading) {
    return (
      <DashboardViewLayout
        breadcrumbs={[{ text: 'Zip Blasts', href: '/dashboard/zip-blasts' }]}
        header={<Header variant="h1">Loading…</Header>}
      >
        <Container />
      </DashboardViewLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardViewLayout
        breadcrumbs={[{ text: 'Zip Blasts', href: '/dashboard/zip-blasts' }]}
        header={<Header variant="h1">Zip blast</Header>}
      >
        <Alert type="error" header="Failed to load">
          {String(error)}
        </Alert>
      </DashboardViewLayout>
    );
  }

  const inFlight = !TERMINAL_STATUSES.has(data.status);
  const scopeLabel = getScopeLabel(data);
  const childZips = data.zip_codes ?? [];
  const isAreaBlast =
    childZips.length > 1 || data.child_count > 0 || Boolean(data.city || data.area_label);
  const childRows: ChildZipRow[] = childZips.map((zip) => ({
    zip,
    role: zip === data.zip_code ? 'Primary' : 'Child ZIP',
  }));
  const childInProgress = Math.max(
    data.child_count - data.completed_child_count - data.failed_child_count,
    0,
  );

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Zip Blasts', href: '/dashboard/zip-blasts' },
        { text: scopeLabel, href: `/dashboard/zip-blasts/${uuid}` },
      ]}
      header={
        <Header
          variant="h1"
          description={`Created ${formatDate(data.created_at)}${
            data.completed_at ? ` · Completed ${formatDate(data.completed_at)}` : ''
          }`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => refetch()}>Refresh</Button>
              {inFlight ? (
                <Button loading={cancelMut.isPending} onClick={() => cancelMut.mutate()}>
                  Cancel
                </Button>
              ) : null}
              {data.status !== 'complete' && data.status !== 'cancelled' ? (
                <Button
                  variant="primary"
                  loading={retryMut.isPending}
                  onClick={() => retryMut.mutate()}
                >
                  {data.status === 'failed' ? 'Retry' : 'Resume'}
                </Button>
              ) : null}
              {data.campaign_uuid ? (
                <Button
                  onClick={() =>
                    navigate({
                      to: '/dashboard/drip/campaigns/$uuid',
                      params: { uuid: data.campaign_uuid as string },
                    })
                  }
                >
                  View campaign
                </Button>
              ) : null}
              <Button
                onClick={() =>
                  navigate({
                    to: '/dashboard/drip/contacts',
                    search: { zip_blast_uuid: uuid },
                  })
                }
              >
                View blast contacts
              </Button>
              <Button
                onClick={() =>
                  navigate({
                    to: '/dashboard/drip/emails',
                    search: { zip_blast_uuid: uuid },
                  })
                }
              >
                View blast emails
              </Button>
            </SpaceBetween>
          }
        >
          Zip blast — {scopeLabel}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">Status</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <KeyValuePairs
              items={[{ label: 'State', value: <ZipBlastStatusBadge status={data.status} /> }]}
            />
            <KeyValuePairs items={[{ label: 'Started', value: formatDate(data.started_at) }]} />
            <KeyValuePairs items={[{ label: 'Completed', value: formatDate(data.completed_at) }]} />
          </ColumnLayout>
          {data.error_message ? (
            <Box padding={{ top: 'm' }}>
              <Alert type="error" header="Error">
                <Box variant="code">{data.error_message}</Box>
              </Alert>
            </Box>
          ) : null}
        </Container>

        <Container header={<Header variant="h2">Scope</Header>}>
          <ColumnLayout columns={4} variant="text-grid">
            <KeyValuePairs items={[{ label: 'Zip code', value: data.zip_code }]} />
            <KeyValuePairs items={[{ label: 'City / area', value: scopeLabel }]} />
            <KeyValuePairs items={[{ label: 'ZIP codes', value: formatZipList(childZips) }]} />
            <KeyValuePairs items={[{ label: 'Daily cap', value: String(data.target_per_day) }]} />
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h2">Progress</Header>}>
          <ColumnLayout columns={6} variant="text-grid">
            <KeyValuePairs
              items={[{ label: 'Listings found', value: String(data.listings_found) }]}
            />
            <KeyValuePairs
              items={[{ label: 'Places resolved', value: String(data.places_resolved) }]}
            />
            <KeyValuePairs
              items={[{ label: 'Reports built', value: String(data.reports_built) }]}
            />
            <KeyValuePairs
              items={[{ label: 'Contacts created', value: String(data.contacts_created) }]}
            />
            <KeyValuePairs items={[{ label: 'Leads pushed', value: String(data.leads_pushed) }]} />
            <KeyValuePairs
              items={[{ label: 'Emails scheduled', value: String(data.emails_scheduled) }]}
            />
          </ColumnLayout>
        </Container>

        {isAreaBlast ? (
          <Container header={<Header variant="h2">Area progress</Header>}>
            <SpaceBetween size="m">
              <ColumnLayout columns={4} variant="text-grid">
                <KeyValuePairs items={[{ label: 'Child ZIPs', value: String(data.child_count) }]} />
                <KeyValuePairs
                  items={[
                    { label: 'Completed child ZIPs', value: String(data.completed_child_count) },
                  ]}
                />
                <KeyValuePairs
                  items={[{ label: 'Failed child ZIPs', value: String(data.failed_child_count) }]}
                />
                <KeyValuePairs
                  items={[{ label: 'In progress child ZIPs', value: String(childInProgress) }]}
                />
              </ColumnLayout>

              <Table
                variant="embedded"
                items={childRows}
                empty={
                  <Box color="inherit" textAlign="center">
                    No child ZIP rows have been reported yet.
                  </Box>
                }
                header={<Header variant="h3">Child ZIPs</Header>}
                columnDefinitions={[
                  { id: 'zip', header: 'ZIP codes', cell: (item) => item.zip, width: 160 },
                  { id: 'role', header: 'Role', cell: (item) => item.role },
                ]}
              />
            </SpaceBetween>
          </Container>
        ) : null}

        <Container header={<Header variant="h2">Skip reasons</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <KeyValuePairs
              items={[{ label: 'Outside area skipped', value: String(data.outside_area_skipped) }]}
            />
          </ColumnLayout>
        </Container>

        {data.notes ? (
          <Container header={<Header variant="h2">Notes</Header>}>
            <Box variant="p">{data.notes}</Box>
          </Container>
        ) : null}

        {data.campaign_uuid ? (
          <Container header={<Header variant="h2">Campaign</Header>}>
            <Link
              href={`/dashboard/drip/campaigns/${data.campaign_uuid}`}
              onFollow={(e) => {
                e.preventDefault();
                navigate({
                  to: '/dashboard/drip/campaigns/$uuid',
                  params: { uuid: data.campaign_uuid as string },
                });
              }}
            >
              {data.campaign_name ?? `Campaign ${data.campaign_uuid}`}
            </Link>
          </Container>
        ) : null}
      </SpaceBetween>
    </DashboardViewLayout>
  );
}
